/**
 * The view of "Last changes" ↔ the URL (CU-869fn0gcp, plan § 4.2; `patterns.md` § 14 write-back):
 * `?office=A1&lor=7&pickup_from=…&pickup_to=…&window=last_7d[&days=N][&unit=day][&sim=0][&sel=2026-11-02[:MDMR]]`.
 * `sim=0` = the simulated filter on Exclude (v1.5); Include, the default, is not written.
 *
 * Read once into state with defaults (the last office used, 7 days, the next 14 days, last 7 days, Total); bad
 * values read as defaults, never a 400 from the server on a shared link. Written back on every change. The deep
 * links out (Activity, Executions) are built here too, with the keys those screens read (`cellFilter.readCell` /
 * `readScope`, `execution_id`). Pure and alias-free: runs under `node --test`.
 */
import { fromIso, toIso } from '../../@services/dateRange.js'

export const WINDOWS = Object.freeze(['last_7d', 'today', 'last_pass', 'custom'])
export const UNITS = Object.freeze(['total', 'day'])
/** The simulated filter (v1.5): Include counts simulated decisions like written ones, Exclude leaves them out. */
export const SIMULATED = Object.freeze(['include', 'exclude'])
export const DEFAULT_SIMULATED = 'include'
export const DEFAULT_WINDOW = 'last_7d'
export const DEFAULT_LOR = 7
export const DEFAULT_PICKUP_DAYS = 14
export const MAX_PICKUP_DAYS = 31
export const MAX_CUSTOM_DAYS = 30
/** "Last N days" when the link carries no N: the default window's 7 days (the server's `DEFAULT_DAYS`). */
export const DEFAULT_CUSTOM_DAYS = 7
export const LOR_MAX = 180
export const STORAGE_OFFICE = 'dsRepricing.lastChanges.office'

const ACRISS = /^[A-Z]{4}$/
const pad = n => String(n).padStart(2, '0')

/** `YYYY-MM-DD` + n days (local), or null. */
export function addDaysIso(iso, n) {
	const d = fromIso(iso)
	if (!d) return null
	d.setDate(d.getDate() + n)
	return toIso(d)
}

/** Whole days from `a` to `b` inclusive (1 for the same day), or null. */
export function spanDays(a, b) {
	const da = fromIso(a), db = fromIso(b)
	if (!da || !db) return null
	return Math.round((db - da) / 86400000) + 1
}

/** The ISO dates of the sample, `from` to `to` inclusive. */
export function datesBetween(from, to) {
	const n = spanDays(from, to)
	if (!n || n < 1) return []
	return Array.from({ length: n }, (_, i) => addDaysIso(from, i))
}

const positiveInt = (v, max) => {
	const t = String(v ?? '').trim()
	if (!/^\d+$/.test(t)) return null
	const n = Number(t)
	return n >= 1 && n <= max ? n : null
}

const office = v => {
	const t = String(v ?? '').trim()
	return t && t.length <= 20 && /^[A-Za-z0-9_-]+$/.test(t) ? t : null
}

/** `sel=2026-11-02` (a pick-up, every ACRISS) or `sel=2026-11-02:MDMR` (one cell) → `{ pickup, sipp }`, or null. */
export function parseSel(raw) {
	const [day, code] = String(raw ?? '').trim().split(':')
	if (!fromIso(day)) return null
	const sipp = String(code || '').toUpperCase()
	return { pickup: day, sipp: ACRISS.test(sipp) ? sipp : null }
}

export function formatSel(sel) {
	if (!sel?.pickup) return null
	return sel.sipp ? `${sel.pickup}:${sel.sipp}` : sel.pickup
}

/** The pick-up pair the band may hold: `to` clamped to 31 days from `from`, order kept. */
export function clampPickup(from, to) {
	if (!fromIso(from)) return null
	let a = from, b = fromIso(to) ? to : addDaysIso(from, DEFAULT_PICKUP_DAYS - 1)
	if (b < a) [a, b] = [b, a]
	if (spanDays(a, b) > MAX_PICKUP_DAYS) b = addDaysIso(a, MAX_PICKUP_DAYS - 1)
	return { from: a, to: b }
}

/**
 * Query string → the view. `today` is the operator's day (ISO); `lastOffice` the office remembered for this
 * account (localStorage), used when the link carries none — `office: null` then means "the first of the catalogue".
 */
export function readView(search, today, { lastOffice = null } = {}) {
	const q = new URLSearchParams(search || '')
	const from = fromIso(q.get('pickup_from')) ? q.get('pickup_from') : today
	const pickup = clampPickup(from, q.get('pickup_to')) || clampPickup(today, null)
	const window = WINDOWS.includes(q.get('window')) ? q.get('window') : DEFAULT_WINDOW
	const days = window === 'custom' ? (positiveInt(q.get('days'), MAX_CUSTOM_DAYS) || DEFAULT_CUSTOM_DAYS) : null
	return {
		office: office(q.get('office')) || office(lastOffice) || null,
		lor: positiveInt(q.get('lor'), LOR_MAX) || DEFAULT_LOR,
		pickup_from: pickup.from,
		pickup_to: pickup.to,
		window,
		days,
		unit: q.get('unit') === 'day' ? 'day' : 'total',
		simulated: q.get('sim') === '0' ? 'exclude' : DEFAULT_SIMULATED,
		sel: parseSel(q.get('sel')),
	}
}

/** The view → `?…` (or `''`), only the keys that differ from nothing: `unit=day` and `days` when they apply. */
export function writeView(view) {
	const q = new URLSearchParams()
	if (view.office) q.set('office', view.office)
	q.set('lor', String(view.lor))
	q.set('pickup_from', view.pickup_from)
	q.set('pickup_to', view.pickup_to)
	q.set('window', view.window)
	if (view.window === 'custom' && view.days) q.set('days', String(view.days))
	if (view.unit === 'day') q.set('unit', 'day')
	if (view.simulated === 'exclude') q.set('sim', '0')
	const sel = formatSel(view.sel)
	if (sel) q.set('sel', sel)
	const s = q.toString()
	return s ? `?${s}` : ''
}

/**
 * The query of `/last-changes` (and the first half of `/pushes`): the server's names, the unit stays here. The simulated
 * filter is always sent (v1.5), so the screen never depends on the server's default.
 */
export function queryOf(view) {
	const q = new URLSearchParams()
	q.set('office', view.office || '')
	q.set('lor', String(view.lor))
	q.set('pickup_from', view.pickup_from)
	q.set('pickup_to', view.pickup_to)
	q.set('window', view.window)
	if (view.window === 'custom' && view.days) q.set('days', String(view.days))
	q.set('simulated', view.simulated === 'exclude' ? 'exclude' : DEFAULT_SIMULATED)
	return q.toString()
}

/** What a reload depends on: the sample, the window and the simulated filter, not the unit nor the selection. */
export function sampleKey(view) {
	return queryOf(view)
}

export function defaultView(today, lastOffice = null) {
	return readView('', today, { lastOffice })
}

const DS = '/ds-repricing'

/** Activity log, filtered as its links are (`cellFilter.readScope` / `readCell`, `execution_id` read on load). */
export function activityHref({ execution_id = null, office, sipp = null, pickup_from, pickup_to = pickup_from, lor }) {
	const q = new URLSearchParams()
	if (execution_id) q.set('execution_id', execution_id)
	if (office) q.set('office', office)
	if (sipp) q.set('sipp', sipp)
	if (pickup_from) q.set('pickup_from', pickup_from)
	if (pickup_to) q.set('pickup_to', pickup_to)
	if (lor != null) q.set('duration', String(lor))
	return `${DS}/activity?${q.toString()}`
}

/** Executions, landing on one pass (read-once `?execution_id=`). */
export function executionsHref(executionId) {
	return `${DS}/executions?execution_id=${encodeURIComponent(executionId || '')}`
}
